import { useCallback } from 'react'
import * as Sentry from '@sentry/electron/renderer'
import { useSubmitMessage } from '@renderer/chat/hooks'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import type { ReminderItem } from '../../../hooks/reminder-mapping'
import { useViewActions } from '../../../view-atoms'
import { buildReminderMetadataText, REMINDER_ATTACHMENT_LABEL } from '../utils/build-draft-prompt'
import { useFetchReminderSourceLines } from './use-start-reminder-chat'

/**
 * Sends the user's own prompt in a new chat about a reminder, with the
 * reminder attached as the chat button attaches it, and opens the chat.
 */
export function useAskAboutReminder() {
  const fetchSourceLines = useFetchReminderSourceLines()
  const submitMessage = useSubmitMessage()
  const { openView } = useViewActions()

  return useCallback(
    async (reminder: ReminderItem, prompt: string, trigger: string) => {
      AmplitudeRenderer.trackEvent('Reminder Action', { type: 'Ask', reminderId: reminder.id, trigger })
      const sourceLines = await fetchSourceLines(reminder)
      await submitMessage(prompt, {
        isNewChat: true,
        attachments: [
          {
            attachment: { type: 'clipboardText', text: buildReminderMetadataText(reminder, sourceLines) },
            metadata: { label: REMINDER_ATTACHMENT_LABEL },
          },
        ],
        onSubmit: (conversationId: string) => {
          openView('chat', { chatId: conversationId, instantScrollToBottom: true }, { sourceLabel: 'To-dos' })
        },
      }).catch((error: unknown) => Sentry.captureException(error))
    },
    [fetchSourceLines, submitMessage, openView],
  )
}
